import { ToolContainer, FileDropZone, DownloadButton, downloadBlob } from "@/components/ToolLayout";
import { useState, useCallback, useMemo, useEffect } from "react";
import { ImageTracerBrowser, Options, CreatePaletteMode } from "@image-tracer-ts/browser";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ensureSvgSize } from "./svgSize";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { NoticeBox } from "@/components/NoticeBox";

const TOOL_INFO_CONTENT = {
  what: `Converts a raster image (like a JPG or PNG) into a real SVG vector file — actual scalable paths traced from your image, not just the original picture embedded inside an SVG wrapper. A true vector can be scaled to any size with no quality loss at all, which is what makes this genuinely useful for a logo or simple graphic that needs to work at both a tiny favicon size and a huge banner. Runs entirely in your browser — the image never leaves your device.\n\nHonestly: this works well for simple, high-contrast art — logos, icons, line drawings. A detailed photo can be traced too, but the tracing process itself is lossy in a different way than resizing is — fine detail and subtle color gradients get simplified into flat color regions, so a traced photo looks noticeably different from the original, not identical-but-scalable.`,
  how: `Upload your image, adjust the number of colors if needed, and press Convert to SVG to trace it into vector paths. Preview and download the resulting SVG file (it has the same pixel width and height as your source image, and still scales to any size). Very large or highly detailed images may take a while to trace, or produce an unwieldy result — simple, high-contrast source images convert fastest and cleanest.`,
  tip: `This works best on simple, high-contrast images like logos or line drawings — a detailed photo will convert but often produces an unwieldy, overly complex SVG rather than a clean result. Fewer colors generally means a cleaner, simpler trace; more colors captures more detail but produces a larger, more complex file.`,
};

// Vectorizing a large, detailed image can take a genuinely long time
// (the tracing algorithm has no progress-reporting hook to show partial
// completion, and no cancel point either) -- rather than letting the
// browser tab appear to hang with no feedback, this warns before
// starting so the choice to proceed with a large/complex source is an
// informed one, not a surprise.
const LARGE_IMAGE_DIMENSION = 1500;

export default function ImageToSvg() {
  const [file, setFile] = useState<File | null>(null);
  const [numberOfColors, setNumberOfColors] = useState([8]);
  const [processing, setProcessing] = useState(false);
  const [svgString, setSvgString] = useState<string | null>(null);
  const [dimensions, setDimensions] = useState<{ w: number; h: number } | null>(null);

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setSvgString(null); setDimensions(null);
    const img = new Image();
    img.onload = () => setDimensions({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = URL.createObjectURL(f);
  }, []);

  // Show the traced SVG through <img> from a blob URL: scripts inside an SVG never run there.
  const svgPreviewUrl = useMemo(
    () => (svgString ? URL.createObjectURL(new Blob([svgString], { type: "image/svg+xml" })) : null),
    [svgString],
  );
  useEffect(() => () => { if (svgPreviewUrl) URL.revokeObjectURL(svgPreviewUrl); }, [svgPreviewUrl]);

  const isLargeImage = dimensions && Math.max(dimensions.w, dimensions.h) > LARGE_IMAGE_DIMENSION;

  const convert = async () => {
    if (!file) return;
    setProcessing(true);
    setSvgString(null);
    try {
      const options: Partial<Options> = {
        colorSamplingMode: CreatePaletteMode.GENERATE,
        numberOfColors: numberOfColors[0],
      };
      const svg = await ImageTracerBrowser.fromFile(file, options);
      setSvgString(ensureSvgSize(svg as string, dimensions?.w, dimensions?.h));
      toast.success("Converted to SVG!");
    } catch (err) {
      console.error("Image to SVG conversion failed:", err);
      toast.error("Couldn't convert that image. Try a different file, or a smaller/simpler image.");
    } finally {
      setProcessing(false);
    }
  };

  const download = () => {
    if (!svgString || !file) return;
    downloadBlob(new Blob([svgString], { type: "image/svg+xml" }), file.name.replace(/\.[^.]+$/, ".svg"));
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image" description="Convert raster images to SVG vector format" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>

          <div className="max-w-sm space-y-2">
            <Label>Colors: {numberOfColors[0]}</Label>
            <Slider value={numberOfColors} onValueChange={(v) => { setNumberOfColors(v); setSvgString(null); }} min={2} max={32} step={1} />
          </div>

          {isLargeImage && !svgString && (
            <NoticeBox className="text-xs">
              This is a large image ({dimensions!.w}×{dimensions!.h}px) — tracing may take a while, and there's no
              way to cancel once started. A simpler or smaller source image (a logo works better than a detailed
              photo) will convert faster and produce a cleaner result.
            </NoticeBox>
          )}

          {processing && (
            <p className="text-xs text-muted-foreground">
              Tracing paths... this can take a little while for a detailed or large image, with no progress bar to
              show partial completion — it hasn't frozen, it's genuinely still working.
            </p>
          )}

          {svgString && (
            <div className="p-4 rounded-lg border bg-card flex items-center justify-center max-h-80 overflow-auto">
              {/* An <img> never runs scripts or event handlers inside the SVG, unlike inserting its markup into the page. */}
              <img src={svgPreviewUrl ?? undefined} alt="Traced SVG preview" className="max-h-72 max-w-full" />
            </div>
          )}

          <div className="flex gap-3">
            {svgString ? (
              <>
                <DownloadButton blob={svgString ? new Blob([svgString], { type: "image/svg+xml" }) : null} fileName={file ? file.name.replace(/\.[^.]+$/, ".svg") : "image.svg"} label="Download SVG" />
                <Button variant="outline" onClick={() => setSvgString(null)}>Re-convert</Button>
                <Button variant="outline" onClick={() => { setFile(null); setSvgString(null); }}>New</Button>
              </>
            ) : (
              <>
                <Button onClick={convert} disabled={processing}>{processing ? "Converting..." : "Convert to SVG"}</Button>
                <Button variant="outline" onClick={() => setFile(null)} disabled={processing}>Reset</Button>
              </>
            )}
          </div>
        </div>
      )}
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
